import { faChevronDown, faChevronUp, faInfoCircle, faPercent, faPlus, faSortDown, faSortUp, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Disclosure } from "@headlessui/react";
import classNames from "classnames";
import isNumber from "lodash/isNumber";
import { Field as BaseField, List } from "rc-field-form";
import Select from "rc-select";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Input, InputGroup, InputGroupText, Row, Tooltip, UncontrolledTooltip } from "reactstrap";
import Button from "../../../../../../components/Button";
import Field from "../../../../../../components/Field";
import DurationInput, { getDurationInputValue } from "../../../../../../components/Inputs/DurationInput";
import MoneyInput from "../../../../../../components/Inputs/MoneyInput";
import ResourceSelect from "../../../../../../components/Inputs/ResourceSelect";
import Switch from "../../../../../../components/Inputs/Switch";
import Text from "../../../../../../components/Inputs/Text";
import { renderDurationAsFormat } from "../../../../../../utils/helpers/date";
import { useOrgSettingsResourceLabel } from "../../../../../../utils/hooks/useOrgSettingsResourceLabel";
import {
    getPathWithParams,
    getSettingsOrgResourceSearchFilters,
    getSettingsResourceSearchFilters,
    getSettingsResourceSearchFiltersWithLookups,
    renderSettingsResourceLabel,
} from "../../../../../../utils/helpers/settings";
import { useCustomFields } from "../../../../CustomFields/context";
import { payCodeCalcFields } from "../../../CalculationGroup/helper";

const getWorkingShiftSearchFilters = (search) => {
    if (!search) {
        return;
    }
    return getSettingsResourceSearchFiltersWithLookups(search);
};

const ShiftRules = ({ rule, removeRule, moveRule, rulesCount, form, conflictMap, groups, ruleIndex }) => {
    const { t } = useTranslation();
    const { peopleCustomFields } = useCustomFields();
    const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const toggleTooltip = () => setTooltipOpen((prev) => !prev);

    return (
        <Disclosure defaultOpen={true}>
            {({ open }) => (
                <>
                    <div
                        className={classNames(
                            "shift-rule-form__rules__item__head",
                            open && "shift-rule-form__rules__item__head--open"
                        )}
                    >
                        <BaseField name={rule.name}>
                            {({ value }) => (
                                <>
                                    <div className="w-75">
                                        <Disclosure.Button
                                            id={`shift-rule-toggle-${ruleIndex}`}
                                            className="d-inline-flex align-items-center p-0 mr-2 border-0 bg-transparent cursor-pointer"
                                        >
                                            <FontAwesomeIcon
                                                className="font-weight-700"
                                                icon={open ? faChevronUp : faChevronDown}
                                            />
                                        </Disclosure.Button>
                                        <Tooltip
                                            target={`shift-rule-toggle-${ruleIndex}`}
                                            isOpen={tooltipOpen}
                                            toggle={toggleTooltip}
                                            placement="top"
                                        >
                                            {open ? t("collapse") : t("expand")}
                                        </Tooltip>

                                        <span className="font-weight-700">
                                            {value?.shift
                                                ? renderSettingsResourceLabel(value?.shift)
                                                : t("no-working-shift")}
                                        </span>

                                        <span> | </span>

                                        <span>{value?.method ? t(value?.method) : t("no-method")}</span>
                                    </div>

                                    <div className="d-flex align-items-center gap-5">
                                        <span id={`shift-rule-sort-up-${ruleIndex}`}>
                                            <FontAwesomeIcon
                                                size="lg"
                                                className={classNames(
                                                    "shift-rule-form__sort-icon mt-2",
                                                    ruleIndex === 0 && "shift-rule-form__sort-icon--disabled"
                                                )}
                                                icon={faSortUp}
                                                onClick={() => ruleIndex > 0 && moveRule(ruleIndex, ruleIndex - 1)}
                                            />
                                        </span>
                                        <UncontrolledTooltip target={`shift-rule-sort-up-${ruleIndex}`} placement="top">
                                            {t("move-up")}
                                        </UncontrolledTooltip>

                                        <span id={`shift-rule-sort-down-${ruleIndex}`}>
                                            <FontAwesomeIcon
                                                size="lg"
                                                className={classNames(
                                                    "shift-rule-form__sort-icon mb-3",
                                                    ruleIndex === rulesCount - 1 && "shift-rule-form__sort-icon--disabled"
                                                )}
                                                icon={faSortDown}
                                                onClick={() => ruleIndex < rulesCount - 1 && moveRule(ruleIndex, ruleIndex + 1)}
                                            />
                                        </span>
                                        <UncontrolledTooltip target={`shift-rule-sort-down-${ruleIndex}`} placement="top">
                                            {t("move-down")}
                                        </UncontrolledTooltip>

                                        <span id={`shift-rule-delete-${ruleIndex}`}>
                                            <FontAwesomeIcon
                                                className="text-red cursor-pointer"
                                                icon={faTrash}
                                                onClick={() => removeRule(rule.name)}
                                            />
                                        </span>
                                        <UncontrolledTooltip target={`shift-rule-delete-${ruleIndex}`} placement="top">
                                            {t("delete-rule")}
                                        </UncontrolledTooltip>
                                    </div>
                                </>
                            )}
                        </BaseField>
                    </div>

                    <Disclosure.Panel className="shift-rule-form__rules__item__content">
                        <Row>
                            <Col md={6}>
                                {/* TODO: Do not allow shifts to conflict with each other in timeframes */}
                                <Field
                                    name={[rule.name, "shift"]}
                                    label={t("shift")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("shift-required"),
                                        },
                                    ]}
                                >
                                    <ResourceSelect
                                        resourcePath="/working-shift"
                                        renderLabel={renderSettingsResourceLabel}
                                        getSearchFilters={getWorkingShiftSearchFilters}
                                        placeholder={t("shift")}
                                        hasSearch
                                    />
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field
                                    name={[rule.name, "payCode"]}
                                    label={t("pay-code")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("pay-code-required"),
                                        },
                                    ]}
                                >
                                    <ResourceSelect
                                        resourcePath={getPathWithParams('/pay-code', true, {}, payCodeCalcFields, { hourType: 'unit' })}
                                        renderLabel={renderSettingsResourceLabel}
                                        getSearchFilters={getSettingsResourceSearchFilters}
                                        placeholder={t("pay-code")}
                                        hasSearch
                                    />
                                </Field>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Field
                                    name={[rule.name, "method"]}
                                    label={t("method")}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("method-required"),
                                        },
                                    ]}
                                >
                                    <Select
                                        placeholder={t("method")}
                                        onChange={() => {
                                            form.resetFields([["rules", rule.name, "value"]]);
                                        }}
                                    >
                                        <Select.Option value="amount-per-hour">
                                            {t("amount-per-hour")}
                                        </Select.Option>
                                        <Select.Option value="amount-per-shift">
                                            {t("amount-per-shift")}
                                        </Select.Option>
                                        <Select.Option value="percentage">{t("percentage")}</Select.Option>
                                    </Select>
                                </Field>
                            </Col>

                            <Col md={6}>
                                <BaseField name={[rule.name, "method"]}>
                                    {({ value }) => {
                                        // TODO: validations and a proper percentage input
                                        switch (value) {
                                            case "amount-per-hour":
                                            case "amount-per-shift": {
                                                return (
                                                    <Field
                                                        name={[rule.name, "value"]}
                                                        label={t("value")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("value-required"),
                                                            },
                                                        ]}
                                                    >
                                                        <MoneyInput
                                                            placeholder={t("value")}
                                                            className="w-100 d-block"
                                                        />
                                                    </Field>
                                                );
                                            }
                                            case "percentage": {
                                                return (
                                                    <Field
                                                        name={[rule.name, "value"]}
                                                        label={t("value")}
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message: t("value-required"),
                                                            },
                                                        ]}
                                                    >
                                                        {(field) => {
                                                            return (
                                                                <InputGroup>
                                                                    <InputGroupText>
                                                                        <FontAwesomeIcon className="px-1" icon={faPercent} />
                                                                    </InputGroupText>
                                                                    <Text type="number" placeholder={t("value")} min={1} {...field} />
                                                                </InputGroup>
                                                            );
                                                        }}
                                                    </Field>
                                                );
                                            }
                                            default: {
                                                return null;
                                            }
                                        }
                                    }}
                                </BaseField>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Field
                                    name={[rule.name, "priority"]}
                                    label={t("priority")}
                                >
                                    {({ value, onChange }) => {
                                        return (
                                            <Input
                                                type="number"
                                                placeholder={t("priority")}
                                                min={1}
                                                max={100}
                                                value={value}
                                                onChange={(e) => {
                                                    onChange(Number(e.target.value));
                                                }}
                                            />
                                        );
                                    }}
                                </Field>
                            </Col>

                            <Col md={6}>
                                <Field
                                    name={[rule.name, "shouldMatchShift"]}
                                    label={
                                        <span>
                                            <span className="mr-2">
                                                {t("should-match-shift")}
                                            </span>
                                            <FontAwesomeIcon
                                                id={`should-match-shift-info-icon-${rule.name}`}
                                                icon={faInfoCircle}
                                                className="cursor-pointer"
                                            />
                                            <UncontrolledTooltip target={`should-match-shift-info-icon-${rule.name}`}>
                                                {t("employee-should-have-the-working-shift-to-get-the-premium")}
                                            </UncontrolledTooltip>
                                        </span>
                                    }
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Field>
                            </Col>
                        </Row>

                        <div className="mb-4">
                            <Field
                                name={[rule.name, "overtime", "status"]}
                                className="bg-light mb-0 p-4"
                                valuePropName="checked"
                            >
                                <Switch>
                                    {t("premium-on-overtime")}
                                </Switch>
                            </Field>

                            <BaseField shouldUpdate>
                                {({ }, { }, { getFieldValue }) => {
                                    const hasOvertime = getFieldValue(["rules", rule.name, "overtime", "status"]);
                                    if (!hasOvertime) {
                                        return null;
                                    }
                                    const hourTypes = ["overtime", "double-time", "double-time-and-half"];
                                    return (
                                        <div className="p-4 pt-0 bg-lighter">
                                            {hourTypes.map((hourType) => {
                                                const isLast = hourTypes.indexOf(hourType) === hourTypes.length - 1;
                                                return (
                                                    <Row key={hourType}>
                                                        <Col md={6} className="align-content-center">
                                                            <Field
                                                                className={classNames(isLast && "mb-0")}
                                                                name={[rule.name, "overtime", hourType, "status"]}
                                                                valuePropName="checked"
                                                            >
                                                                <Switch>
                                                                    {t(hourType)}
                                                                </Switch>
                                                            </Field>
                                                        </Col>

                                                        <Col md={6}>
                                                            <BaseField shouldUpdate>
                                                                {({ }, { }, { getFieldValue }) => {
                                                                    const hasOvertime = getFieldValue(["rules", rule.name, "overtime", hourType, "status"]);
                                                                    if (!hasOvertime) {
                                                                        return null;
                                                                    }
                                                                    return (
                                                                        <Field
                                                                            className={classNames(isLast && "mb-0")}
                                                                            name={[rule.name, "overtime", hourType, "payCode"]}
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: t("pay-code-required"),
                                                                                }
                                                                            ]}
                                                                        >
                                                                            <ResourceSelect
                                                                                resourcePath={getPathWithParams('/pay-code', true, {}, payCodeCalcFields, { hourType: 'unit' })}
                                                                                renderLabel={renderSettingsResourceLabel}
                                                                                getSearchFilters={getSettingsResourceSearchFilters}
                                                                                placeholder={t("pay-code")}
                                                                                hasSearch
                                                                            />
                                                                        </Field>
                                                                    )
                                                                }}
                                                            </BaseField>
                                                        </Col>
                                                    </Row>
                                                );
                                            })}
                                        </div>
                                    );
                                }}
                            </BaseField>
                        </div>

                        <Row>
                            <Col md={2}>
                                <Field
                                    name={[rule.name, "minimum", "status"]}
                                    label={t("minimum")}
                                    valuePropName="checked"
                                >
                                    <Switch
                                        onChange={(e) => {
                                            if (!e.target.checked) {
                                                form.resetFields([
                                                    ["rules", rule.name, "minimum", "type"],
                                                    ["rules", rule.name, "minimum", "value"],
                                                    ["rules", rule.name, "minimum", "continuous"],
                                                ]);
                                            }
                                        }}
                                    />
                                </Field>
                            </Col>

                            <Col md={2}>
                                <BaseField shouldUpdate>
                                    {({ }, { }, { getFieldValue }) => {
                                        const status = getFieldValue(["rules", rule.name, "minimum", "status"]);
                                        return (
                                            <Field
                                                name={[rule.name, "minimum", "continuous"]}
                                                label={t("continuous")}
                                                valuePropName="checked"
                                            >
                                                <Switch disabled={!status} />
                                            </Field>
                                        );
                                    }}
                                </BaseField>
                            </Col>

                            <Col md={4}>
                                <BaseField shouldUpdate>
                                    {({ }, { }, { getFieldValue }) => {
                                        const status = getFieldValue(["rules", rule.name, "minimum", "status"]);
                                        return (
                                            <Field
                                                name={[rule.name, "minimum", "type"]}
                                                label={t("type")}
                                                rules={[
                                                    {
                                                        required: !!status,
                                                        message: t("type-required"),
                                                    },
                                                ]}
                                            >
                                                <Select
                                                    placeholder={t("type")}
                                                    disabled={!status}
                                                    onChange={() => {
                                                        form.resetFields([
                                                            ["rules", rule.name, "minimum", "value"],
                                                        ]);
                                                    }}
                                                >
                                                    <Select.Option value="duration">{t("duration")}</Select.Option>
                                                    <Select.Option value="percentage">{t("percentage")}</Select.Option>
                                                </Select>
                                            </Field>
                                        );
                                    }}
                                </BaseField>
                            </Col>

                            <Col md={4}>
                                <BaseField shouldUpdate>
                                    {({ }, { }, { getFieldValue }) => {
                                        const status = getFieldValue(["rules", rule.name, "minimum", "status"]);
                                        const type = getFieldValue(["rules", rule.name, "minimum", "type"]);

                                        if (!status || !type) {
                                            return null;
                                        }

                                        return (
                                            <Field
                                                name={[rule.name, "minimum", "value"]}
                                                label={t("value")}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t("value-required"),
                                                    },
                                                ]}
                                            >
                                                {({ value, onChange }) => {
                                                    switch (type) {
                                                        case "duration": {
                                                            return (
                                                                <DurationInput
                                                                    value={value ? renderDurationAsFormat(value, 'HH:mm') : undefined}
                                                                    timeFormat="HH:mm"
                                                                    className="time-input"
                                                                    onChange={(value) => {
                                                                        value = getDurationInputValue(value);
                                                                        onChange(value);
                                                                    }}
                                                                />
                                                            );
                                                        }
                                                        case "percentage": {
                                                            return (
                                                                <InputGroup>
                                                                    <InputGroupText>
                                                                        <FontAwesomeIcon className="px-1" icon={faPercent} />
                                                                    </InputGroupText>
                                                                    <Text
                                                                        type="number"
                                                                        placeholder={t("value")}
                                                                        min={1}
                                                                        disabled={!status}
                                                                        value={isNumber(value) ? value * 100 : undefined}
                                                                        onChange={(e) => {
                                                                            onChange(Number(e.target.value) / 100);
                                                                        }}
                                                                    />
                                                                </InputGroup>
                                                            );
                                                        }
                                                    }
                                                }}
                                            </Field>
                                        );
                                    }}
                                </BaseField>
                            </Col>
                        </Row>

                        <List name={[rule.name, "conditions"]}>
                            {(conditions, { add: addCondition, remove: removeCondition }) => {
                                return (
                                    <div className="shift-rule-form__rules__item__content__conditions">
                                        {conditions.map((condition, conditionIndex) => {
                                            return (
                                                <div
                                                    key={condition.key}
                                                    className="shift-rule-form__rules__item__content__conditions__item"
                                                >
                                                    <Row>
                                                        <Col md={6}>
                                                            <Field
                                                                name={[condition.name, "resource"]}
                                                                label={t("resource")}
                                                                rules={[
                                                                    {
                                                                        required: true,
                                                                        message: t("resource-required"),
                                                                    },
                                                                ]}
                                                            >
                                                                <Select
                                                                    placeholder={t("resource")}
                                                                    onChange={() => {
                                                                        form.resetFields([
                                                                            [
                                                                                "rules",
                                                                                rule.name,
                                                                                "conditions",
                                                                                condition.name,
                                                                                "value",
                                                                            ],
                                                                        ]);
                                                                    }}
                                                                >
                                                                    <Select.Option value="job">
                                                                        {t("job")}
                                                                    </Select.Option>
                                                                    <Select.Option value="location">
                                                                        {t("location")}
                                                                    </Select.Option>
                                                                    <Select.Option value="union">
                                                                        {t("union")}
                                                                    </Select.Option>
                                                                    <Select.Option value="employee-type">
                                                                        {t("employee-type")}
                                                                    </Select.Option>
                                                                    <Select.Option value="employment-type">
                                                                        {t("employment-type")}
                                                                    </Select.Option>
                                                                    {groups.map((group) => (
                                                                        <Select.Option value={group.level}>
                                                                            {renderSettingsResourceLabel(group)}
                                                                        </Select.Option>
                                                                    ))}
                                                                    {peopleCustomFields?.map((field) => (
                                                                        <Select.Option value={`customField.${field?.code}`} key={field?.code}>
                                                                            {renderSettingsResourceLabel(field)}
                                                                        </Select.Option>
                                                                    ))}
                                                                    <Select.Option value="pay-code">
                                                                        {t("payCode")}
                                                                    </Select.Option>
                                                                </Select>
                                                            </Field>
                                                        </Col>

                                                        <Col md={6}>
                                                            <BaseField name={[condition.name, "resource"]}>
                                                                {({ value }) => {
                                                                    const field = peopleCustomFields?.find(({ code }) => value === `customField.${code}`);
                                                                    switch (value) {
                                                                        case "job": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("jobs")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message: t("value-required"),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/jobs"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("jobs")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "location": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("locations")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/locations"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("locations")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "union": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("unions")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/union"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("unions")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case `customField.${field?.code}`: {
                                                                            const rules = [{ required: field?.isRequired, message: t("required-value") }];
                                                                            if (field?.type === "number") {
                                                                                rules.push({
                                                                                    validator: (_, value) => {
                                                                                        if (value && Number(value) < 0) {
                                                                                            return Promise.reject(new Error(t("readableField-bigger-than-zero", { readableField: t("value") })));
                                                                                        }
                                                                                        return Promise.resolve();
                                                                                    }
                                                                                });
                                                                            }

                                                                            if (field?.type === "email") {
                                                                                rules.push({ type: "email", message: t("invalid-email") });
                                                                            }

                                                                            return (
                                                                                <Field
                                                                                    label={field.code}
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    rules={rules}
                                                                                >
                                                                                    {field?.type === "select" ? (
                                                                                        <Select>
                                                                                            {field.options.map((option) => (
                                                                                                <Select.Option value={option.value} key={option.value}>
                                                                                                    {option.label}
                                                                                                </Select.Option>
                                                                                            ))}
                                                                                        </Select>
                                                                                    ) : (
                                                                                        <Text type={field.type} />
                                                                                    )}
                                                                                </Field>
                                                                            )
                                                                        }
                                                                        case "employment-type": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("employee-types")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/employee-type"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("employee-types")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "employment-type": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("employment-types")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/user-demographic/employment-type"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("employment-types")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        case "pay-code": {
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={t("pay-codes")}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath="/pay-code"
                                                                                        mode="multiple"
                                                                                        renderLabel={renderSettingsResourceLabel}
                                                                                        getSearchFilters={getSettingsResourceSearchFilters}
                                                                                        placeholder={t("pay-codes")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                        default: {
                                                                            const group = groups.find(
                                                                                (group) => group.level === value
                                                                            );
                                                                            if (!group) {
                                                                                break;
                                                                            }
                                                                            return (
                                                                                <Field
                                                                                    name={[
                                                                                        condition.name,
                                                                                        "value",
                                                                                    ]}
                                                                                    label={renderSettingsResourceLabel(group)}
                                                                                    rules={[
                                                                                        {
                                                                                            required: true,
                                                                                            message:
                                                                                                t(
                                                                                                    "value-required"
                                                                                                ),
                                                                                        },
                                                                                    ]}
                                                                                >
                                                                                    <ResourceSelect
                                                                                        resourcePath={`${group.id}/org-levels`}
                                                                                        mode="multiple"
                                                                                        renderLabel={
                                                                                            renderOrgLevelResourceLabel
                                                                                        }
                                                                                        getSearchFilters={getSettingsOrgResourceSearchFilters}
                                                                                        placeholder={t("levels")}
                                                                                        hasSearch
                                                                                    />
                                                                                </Field>
                                                                            );
                                                                        }
                                                                    }
                                                                }}
                                                            </BaseField>
                                                        </Col>

                                                        {conflictMap?.[ruleIndex]?.[conditionIndex] && (
                                                            <Col md={12} className="mb-4">
                                                                <span className="text-red">
                                                                    {t("condition-should-be-unique")}
                                                                </span>
                                                            </Col>
                                                        )}

                                                        <Col md={12}>
                                                            <Button
                                                                color="danger"
                                                                className="w-100 d-flex justify-content-center"
                                                                icon={<FontAwesomeIcon icon={faTrash} />}
                                                                onClick={() => removeCondition(condition.name)}
                                                            >
                                                                {t("remove-condition")}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            );
                                        })}

                                        <Row>
                                            <Col md={6}>
                                                <Button
                                                    className="w-100 d-flex justify-content-center"
                                                    icon={<FontAwesomeIcon icon={faPlus} />}
                                                    onClick={() => addCondition({ resource: "job" })}
                                                >
                                                    {t("add-condition")}
                                                </Button>
                                            </Col>

                                            <Col md={6}>
                                                <Button
                                                    color="danger"
                                                    className="w-100 d-flex justify-content-center"
                                                    icon={<FontAwesomeIcon icon={faTrash} />}
                                                    onClick={() => removeRule(rule.name)}
                                                >
                                                    {t("remove-rule")}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            }}
                        </List>
                    </Disclosure.Panel>
                </>
            )}
        </Disclosure>
    );
};

export default ShiftRules;
